import { useState, useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X, Search } from 'lucide-react';
import { StatusFilterCheckboxes } from './StatusFilterCheckboxes';
import type { TicketStatus } from './TicketStatusBadge';
import type { TicketPriority } from './TicketPriorityBadge';

type SortBy = 'created_at' | 'updated_at' | 'ai_priority';

interface TicketFiltersState {
  /** Selected statuses for multi-select filtering. Empty = all statuses. */
  statuses: TicketStatus[];
  /** 'unset' = tickets with no human priority yet (the default for new tickets). */
  priority: TicketPriority | 'unset' | 'all';
  categoryId: string | 'all';
  sortBy?: SortBy;
  search?: string;
}

interface Category {
  categoryId: string;
  name: string;
}

interface TicketFiltersProps {
  filters: TicketFiltersState;
  onFilterChange: (filters: TicketFiltersState) => void;
  categories: Category[];
}

const statusCheckboxOptions: { value: TicketStatus; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting_on_customer', label: 'Waiting on Customer' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const priorityOptions: { value: TicketPriority | 'unset' | 'all'; label: string }[] = [
  { value: 'all', label: 'All Priorities' },
  { value: 'unset', label: 'Unset' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

const sortOptions: { value: SortBy | 'default'; label: string }[] = [
  { value: 'default', label: 'Most Recent' },
  { value: 'updated_at', label: 'Recently Updated' },
  { value: 'ai_priority', label: 'AI Priority' },
];

export function TicketFilters({ filters, onFilterChange, categories }: TicketFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search || '');

  // Keep the local input in sync if the filter is reset/changed externally
  // (e.g. restored from the URL on navigation).
  useEffect(() => {
    setSearchInput(filters.search || '');
  }, [filters.search]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== (filters.search || '')) {
        onFilterChange({ ...filters, search: searchInput || undefined });
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput, filters, onFilterChange]);

  const hasActiveFilters =
    filters.statuses.length > 0 ||
    filters.priority !== 'all' ||
    filters.categoryId !== 'all' ||
    !!filters.sortBy ||
    !!filters.search;

  const handleReset = () => {
    setSearchInput('');
    onFilterChange({
      statuses: [],
      priority: 'all',
      categoryId: 'all',
      sortBy: undefined,
      search: undefined,
    });
  };

  return (
    <div className="space-y-3">
      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search tickets by subject or description..."
          className="pl-9"
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Status filter (multi-select; all checked = no filter) */}
      <StatusFilterCheckboxes
        options={statusCheckboxOptions}
        selected={filters.statuses}
        onChange={(next) => onFilterChange({ ...filters, statuses: next as TicketStatus[] })}
      />

      {/* Remaining single-select filters */}
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
        <Select
          value={filters.priority}
          onValueChange={(value) =>
            onFilterChange({ ...filters, priority: value as TicketPriority | 'unset' | 'all' })
          }
        >
          <SelectTrigger className="w-full sm:w-[160px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            {priorityOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.categoryId}
          onValueChange={(value) => onFilterChange({ ...filters, categoryId: value })}
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.categoryId} value={category.categoryId}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={filters.sortBy || 'default'}
          onValueChange={(value) =>
            onFilterChange({
              ...filters,
              sortBy: value === 'default' ? undefined : (value as SortBy),
            })
          }
        >
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            {sortOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            className="col-span-2 justify-self-start sm:col-auto sm:justify-self-auto"
          >
            <X className="h-4 w-4 mr-1" />
            Clear filters
          </Button>
        )}
      </div>
    </div>
  );
}

export type { TicketFiltersState, Category };
